import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

import { suggestIndustrialRefBook } from '@/api/endpoints/industrialRefBook';
import type { IndustrialRefBookSuggestion } from '@/types/industrialRefBook';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;
const DEFAULT_LIMIT = 10;

interface UseIndustrialRefBookSuggestValue {
  readonly items: readonly IndustrialRefBookSuggestion[];
  readonly loading: boolean;
}

/**
 * Debounced suggest hook against `/industrial-ref-book/suggest`.
 *
 * Activates once `query.trim().length >= 3` and returns up to
 * `limit` items. Earlier in-flight requests are aborted via a
 * sequence-counter so a slow response can't overwrite a fresh one.
 */
export function useIndustrialRefBookSuggest(
  query: string,
  limit: number = DEFAULT_LIMIT,
): UseIndustrialRefBookSuggestValue {
  const [items, setItems] = useState<readonly IndustrialRefBookSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setLoading(false);
      controllerRef.current?.abort();
      return undefined;
    }

    const mySeq = ++seqRef.current;
    setLoading(true);

    const timer = window.setTimeout(() => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      suggestIndustrialRefBook(trimmed, limit, controller.signal)
        .then((response) => {
          if (controller.signal.aborted || seqRef.current !== mySeq) return;
          setItems(response.items);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || axios.isCancel(err)) return;
          if (seqRef.current !== mySeq) return;
          setItems([]);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [query, limit]);

  return { items, loading };
}
